import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, fmtDate, inr } from "@/components/common";
import DataTable from "@/components/DataTable";
import AdvancedFilters from "@/components/AdvancedFilters";
import MultiSelect from "@/components/MultiSelect";
import QuickFilters from "@/components/QuickFilters";

const QF = [{ key: "counselor_name", label: "Counselors" }, { key: "place", label: "Places" }, { key: "seat_type", label: "Seat Types" }, { key: "status", label: "Statuses" }, { key: "source", label: "Sources" }, { key: "last_disposition", label: "Last Dispositions" }];
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { assignable } from "@/lib/assignees";

const EDIT = [["student_name", "Student", "text", 2], ["college_name", "College", "text", 2], ["course", "Course", "text"], ["place", "Place", "text"],
  ["total_fees", "Total Fee", "number"], ["fees_paid", "Paid", "number"], ["scholarship_amount", "Scholarship", "number"], ["admission_date", "Admission Date", "date"],
  ["seat_type", "Seat Type", "text"], ["source", "Source", "text"], ["remark", "Remark", "text", 2]];

function EditAdmission({ adm, onClose, onSaved }) {
  const [f, setF] = useState({});
  const [users, setUsers] = useState([]);
  useEffect(() => { if (adm) setF({ ...adm, total_fees: adm.total_fees ?? adm.fee, fees_paid: adm.fees_paid ?? adm.paid, admission_date: (adm.admission_date || "").slice(0, 10) }); }, [adm]);
  useEffect(() => { api.get("/users").then(({ data }) => setUsers(assignable(data))).catch(() => {}); }, []);
  const save = async () => {
    try {
      const body = Object.fromEntries(EDIT.map(([k]) => [k, f[k] ?? ""]));
      if (f.counselor_id) body.counselor_id = f.counselor_id;
      await api.patch(`/admissions/${adm.id}`, body); toast.success("Admission updated"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!adm} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit Admission · {adm?.code}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {EDIT.map(([k, l, t, span]) => <div key={k} className={span === 2 ? "col-span-2" : ""}><Label>{l}</Label><Input type={t} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`adm-edit-${k}`} /></div>)}
          <div className="col-span-2"><Label>Counselor</Label>
            <select value={f.counselor_id || ""} onChange={(e) => setF({ ...f, counselor_id: e.target.value })} className="w-full h-10 border border-slate-200 rounded-md px-2 text-sm bg-white" data-testid="adm-edit-counselor">
              <option value="">— Select —</option>{users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select></div>
        </div>
        <DialogFooter><Button onClick={save} className="bg-[#4F46E5] hover:bg-[#4338CA]" data-testid="adm-edit-save">Save Changes</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Admissions() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [display, setDisplay] = useState(null);
  const [quick, setQuick] = useState(null);
  const [colF, setColF] = useState([]);
  const [courseF, setCourseF] = useState([]);
  const colOpts = useMemo(() => [...new Set((rows || []).map((r) => r.college_name).filter(Boolean))].sort(), [rows]);
  const courseOpts = useMemo(() => [...new Set((rows || []).filter((r) => !colF.length || colF.includes(r.college_name)).map((r) => r.course).filter(Boolean))].sort(), [rows, colF]);
  useEffect(() => { setCourseF((cf) => cf.filter((x) => courseOpts.includes(x))); }, [courseOpts]);
  const baseRows = useMemo(() => (rows === null ? null : rows.filter((r) => (!colF.length || colF.includes(r.college_name)) && (!courseF.length || courseF.includes(r.course)))), [rows, colF, courseF]);
  const ADM_FIELDS = useMemo(() => [
    { key: "student_name", label: "Student", type: "text" },
    { key: "college_name", label: "College", type: "text" },
    { key: "course", label: "Course", type: "text" },
    { key: "place", label: "Place", type: "text" },
    { key: "counselor_name", label: "Counselor", type: "text" },
    { key: "total_fees", label: "Total Fee", type: "number" },
    { key: "fees_paid", label: "Paid", type: "number" },
    { key: "balance", label: "Balance", type: "number" },
    { key: "scholarship_amount", label: "Scholarship Amount", type: "number" },
    { key: "source", label: "Source", type: "text" },
    { key: "remark", label: "Remark", type: "text" },
    { key: "admission_date", label: "Admission Date", type: "date" },
  ], []);
  const [sel, setSel] = useState(null);
  const [editing, setEditing] = useState(null);
  const { user } = useAuth();
  const canManage = ["super_admin", "admin", "team_lead"].includes(user.role);
  const remove = async (r) => {
    if (!window.confirm(`Delete admission ${r.code} for ${r.student_name}? Its payment, consultation charge and incentive records are removed too.`)) return;
    try { await api.delete(`/admissions/${r.id}`); toast.success("Admission deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const [f, setF] = useState({ amount: "", paid_date: "", description: "" });
  const load = () => api.get("/admissions").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const addIns = async () => {
    try {
      const { data } = await api.post(`/admissions/${sel.id}/instalments`, { ...f, amount: Number(f.amount) });
      toast.success("Instalment added"); setSel(data); setF({ amount: "", paid_date: "", description: "" }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const columns = [
    { key: "code", label: "Admission", fixed: true, sortKey: "student_name", render: (r) => <div><div className="font-semibold text-slate-800">{r.student_name}</div><div className="text-[11px] font-mono text-slate-400">{r.code}{r.place ? ` · ${r.place}` : ""}</div></div> },
    { key: "college_name", label: "College" },
    { key: "course", label: "Course" },
    { key: "fee", label: "Total Fee", sortKey: "total_fees", render: (r) => inr(r.total_fees || r.fee) },
    { key: "fees_paid", label: "Paid", render: (r) => <span className="text-emerald-700 font-semibold">{inr(r.fees_paid ?? r.paid)}</span> },
    { key: "balance", label: "Balance", render: (r) => <span className="text-rose-600">{inr(r.balance ?? Math.max((r.fee || 0) - (r.paid || 0), 0))}</span> },
    { key: "counselor_name", label: "Counselor" },
    { key: "admission_date", label: "Date", render: (r) => <span className="text-xs">{fmtDate(r.admission_date)}</span> },
    { key: "place", label: "Place", hidden: true },
    { key: "scholarship_amount", label: "Scholarship", hidden: true, render: (r) => inr(r.scholarship_amount) },
    { key: "instalments", label: "Instalments", hidden: true, sortable: false, render: (r) => (r.instalments || []).length },
    { key: "remark", label: "Remark", hidden: true, render: (r) => <span className="text-xs max-w-[240px] truncate inline-block">{r.remark || "—"}</span> },
    { key: "source", label: "Source", hidden: true },
    { key: "seat_type", label: "Seat Type", hidden: true },
    { key: "status", label: "Status", hidden: true },
    { key: "last_disposition", label: "Last Disposition", hidden: true, render: (r) => r.last_disposition || "—" },
    { key: "act", label: "", fixed: true, render: (r) => <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <Button size="sm" variant="outline" onClick={() => setSel(r)} data-testid={`adm-ins-${r.id}`}>Instalments</Button>
      {canManage && <button onClick={() => setEditing(r)} className="w-8 h-8 rounded-lg hover:bg-indigo-50 text-[#4F46E5] flex items-center justify-center" title="Edit" data-testid={`adm-edit-${r.id}`}><Pencil className="w-4 h-4" /></button>}
      {canManage && <button onClick={() => remove(r)} className="w-8 h-8 rounded-lg hover:bg-rose-50 text-rose-500 flex items-center justify-center" title="Delete" data-testid={`adm-delete-${r.id}`}><Trash2 className="w-4 h-4" /></button>}
    </div> },
  ];
  return (
    <div>
      <PageHeader title="Admissions" subtitle="Confirmed admissions — the primary business outcome" testid="admissions-header" />
      <div className="flex flex-wrap gap-2 mb-3">
        <MultiSelect label="Filter colleges" options={colOpts} value={colF} onChange={setColF} testid="adm-college-filter" width="w-64" />
        <MultiSelect label="Filter courses" options={courseOpts} value={courseF} onChange={setCourseF} testid="adm-course-filter" width="w-64" />
        {(colF.length > 0 || courseF.length > 0) && <Button variant="ghost" size="sm" onClick={() => { setColF([]); setCourseF([]); }} data-testid="adm-filter-clear">Clear</Button>}
      </div>
      <QuickFilters rows={baseRows} fields={QF} onFiltered={setQuick} testid="admissions" />
      <AdvancedFilters entity="admissions" fields={ADM_FIELDS} rows={quick} onFiltered={setDisplay} />
      <DataTable columns={columns} rows={display} loading={rows === null} testid="admissions-table" columnsKey="admissions" sortable onRowClick={(r) => (r.student_id ? navigate(`/students/${r.student_id}`) : setSel(r))} />
      <EditAdmission adm={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>{sel?.student_name} · Fee Instalments</DialogTitle></DialogHeader>
          {sel && <>
            <div className="text-sm grid grid-cols-3 gap-2"><div>Total <b>{inr(sel.total_fees || sel.fee)}</b></div><div>Paid <b className="text-emerald-700">{inr(sel.fees_paid ?? sel.paid)}</b></div><div>Balance <b className="text-rose-600">{inr(sel.balance)}</b></div></div>
            {sel.remark && <p className="text-xs text-slate-500">{sel.remark}</p>}
            <div className="space-y-1" data-testid="ins-list">{(sel.instalments || []).map((i, k) => <div key={k} className="flex justify-between text-sm border-b border-slate-100 py-1"><span>{fmtDate(i.paid_date)} · {i.description || "—"}</span><b>{inr(i.amount)}</b></div>)}{!sel.instalments?.length && <div className="text-xs text-slate-400">No instalments yet</div>}</div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label>Amount</Label><Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="ins-amount" /></div>
              <div><Label>Paid on</Label><Input type="date" value={f.paid_date} onChange={(e) => setF({ ...f, paid_date: e.target.value })} data-testid="ins-date" /></div>
              <div><Label>Note</Label><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="ins-desc" /></div>
            </div>
          </>}
          <DialogFooter><Button onClick={addIns} className="bg-[#4F46E5] hover:bg-[#4338CA]" data-testid="ins-save"><Plus className="w-4 h-4 mr-1" /> Add Instalment</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
